import { useEffect, useMemo, useState } from "react";
import { api, formatZAR } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AdminOtpIntake from "@/components/AdminOtpIntake";
import { toast } from "sonner";

// Phase OTP-3 — final matter status vocabulary. Status pills also work as
// quick filters that deeplink via ?status=… query param.
const COLUMNS = [
  { key: "new",         label: "New" },
  { key: "intake",      label: "Intake" },
  { key: "in_progress", label: "In progress" },
  { key: "lodged",      label: "Lodged" },
  { key: "completed",   label: "Completed" },
];

const OWNER_OPTIONS = ["all", "client", "back_office", "lawyer", "system"];
const MATTER_TYPE_OPTIONS = ["all", "conveyancing", "antenuptial", "trust", "will", "deceased_estate"];

export default function Matters() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");
  const [onlyAttention, setOnlyAttention] = useState(true);
  // Phase 5.3 — lawyers default to lawyer-owned tasks; others see all
  const [ownerFilter, setOwnerFilter]   = useState(user?.role === "lawyer" ? "lawyer" : "all");
  const [matterTypeFilter, setMatterTypeFilter] = useState("all");
  // Phase OTP-3 — deeplinkable status filter; ?status=new highlights the
  // "New" column (replaces the legacy Leads page).
  const statusFilter = searchParams.get("status") || "all";

  const load = async () => {
    setLoading(true);
    const params = {};
    if (ownerFilter && ownerFilter !== "all") params.owner = ownerFilter;
    if (matterTypeFilter && matterTypeFilter !== "all") params.matter_type = matterTypeFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    try {
      const { data } = await api.get("/matters", { params });
      setMatters(data);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [ownerFilter, matterTypeFilter, statusFilter]);

  // Phase 5.2/5.3 — "Needs attention" filter
  const filteredMatters = useMemo(() => {
    if (!onlyAttention) return matters;
    // Lawyers — always show only exception/attention items
    return matters.filter(
      (m) =>
        m.risk_profile === "high" ||
        (m.blocked_count || 0) > 0 ||
        (m.pending_blockers || 0) > 0 ||
        m.confidence_level === "low" ||
        ["urgent", "high"].includes(m.priority_level),
    );
  }, [matters, onlyAttention]);

  const move = async (id, status) => {
    try {
      await api.patch(`/matters/${id}/status`, { status });
      toast.success(`Moved to ${status.replace("_", " ")}`);
      load();
    } catch {
      toast.error("Failed to move");
    }
  };

  const handleDrop = (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("matter-id");
    if (id) move(id, status);
  };

  return (
    <div className="px-6 md:px-10 py-10 max-w-[1400px] mx-auto" data-testid="admin-matters">
      <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
        <div>
          <div className="text-overline text-zinc-500">Matters</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">Workflow board</h1>
          <p className="mt-2 text-sm text-zinc-600">Drag a matter card to update its status. Each move triggers a client notification.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {user?.role !== "lawyer" && <AdminOtpIntake onCreated={load} />}
          <label className="inline-flex items-center gap-2 text-xs text-zinc-700 select-none" data-testid="filter-attention-label">
            <input
              type="checkbox"
              checked={onlyAttention}
              onChange={(e) => setOnlyAttention(e.target.checked)}
              className="h-4 w-4 accent-zinc-900"
              data-testid="filter-attention"
            />
            Needs attention only <span className="text-zinc-400">({filteredMatters.length}/{matters.length})</span>
          </label>
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="text-xs border border-zinc-200 px-2 py-1.5 bg-white"
            data-testid="filter-owner"
          >
            {OWNER_OPTIONS.map((o) => <option key={o} value={o}>owner: {o}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              const v = e.target.value;
              setSearchParams(v === "all" ? {} : { status: v });
            }}
            className="text-xs border border-zinc-200 px-2 py-1.5 bg-white"
            data-testid="filter-status"
          >
            <option value="all">status: all</option>
            {COLUMNS.map((c) => <option key={c.key} value={c.key}>status: {c.label}</option>)}
          </select>
          <select
            value={matterTypeFilter}
            onChange={(e) => setMatterTypeFilter(e.target.value)}
            className="text-xs border border-zinc-200 px-2 py-1.5 bg-white"
            data-testid="filter-matter-type"
          >
            {MATTER_TYPE_OPTIONS.map((o) => <option key={o} value={o}>type: {o}</option>)}
          </select>
          <div className="flex border border-zinc-200">
            {["kanban", "list"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-4 py-2 text-sm capitalize ${view === v ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                data-testid={`view-${v}`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-5 gap-3 min-h-[60vh]">
          {COLUMNS.map((col) => {
            const items = matters.filter((m) => m.status === col.key);
            return (
              <div
                key={col.key}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, col.key)}
                className="bg-zinc-100 border border-zinc-200 p-3 min-h-[60vh]"
                data-testid={`kanban-col-${col.key}`}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="text-overline text-zinc-600">{col.label}</div>
                  <span className="text-xs px-2 py-0.5 bg-white border border-zinc-200">
                    {loading ? "—" : items.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {loading
                    ? Array.from({ length: 3 }).map((_, i) => (
                        <div
                          key={i}
                          className="bg-white border-l-2 border-l-zinc-200 border border-zinc-200 p-3 animate-pulse"
                          data-testid={`matter-card-skeleton-${col.key}-${i}`}
                        >
                          <div className="h-3 w-32 bg-zinc-200" />
                          <div className="h-2 w-20 bg-zinc-100 mt-2" />
                          <div className="flex gap-1 mt-3">
                            <div className="h-3 w-12 bg-zinc-100" />
                            <div className="h-3 w-10 bg-zinc-100" />
                          </div>
                        </div>
                      ))
                    : items.map((m) => {
                    const risk = m.risk_profile || "medium";
                    const riskCls = risk === "high" ? "bg-red-100 text-red-700" : risk === "low" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700";
                    const prio = m.priority_level || "medium";
                    const prioCls = prio === "urgent" ? "bg-red-600 text-white" : prio === "high" ? "bg-amber-600 text-white" : prio === "medium" ? "bg-zinc-200 text-zinc-800" : "bg-zinc-100 text-zinc-600";
                    const blockedBadge = (m.blocked_count || 0) > 0 || (m.pending_blockers || 0) > 0;
                    return (
                      <div
                        key={m.id}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("matter-id", m.id)}
                        className={`bg-white border-l-2 ${risk === "high" ? "border-l-red-600" : risk === "low" ? "border-l-green-500" : "border-l-amber-500"} border border-zinc-200 p-3 hover:-translate-y-0.5 transition-transform cursor-grab active:cursor-grabbing`}
                        data-testid={`matter-card-${m.id}`}
                      >
                        <Link to={`/admin/matters/${m.id}`} className="block">
                          <div className="text-sm font-medium truncate">{m.client_name || "Unnamed"}</div>
                          <div className="text-[10px] tracking-widest uppercase text-zinc-500 mt-1 truncate">
                            {m.matter_type || m.service_type}
                            {m.pricing_mode && (
                              <span className="ml-2 normal-case tracking-normal text-zinc-400">· {m.pricing_mode}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 mt-2 flex-wrap">
                            <span className={`text-[9px] tracking-wider uppercase px-1.5 py-0.5 ${prioCls}`} data-testid={`card-priority-${m.id}`}>
                              {prio}
                            </span>
                            <span className={`text-[9px] tracking-wider uppercase px-1.5 py-0.5 ${riskCls}`}>
                              risk: {risk}
                            </span>
                            {blockedBadge && (
                              <span className="text-[9px] tracking-wider uppercase px-1.5 py-0.5 bg-red-100 text-red-700">
                                blocked: {m.blocked_count || m.pending_blockers}
                              </span>
                            )}
                          </div>
                          {/* Phase 5 — prediction metadata */}
                          {m.expected_completion_display && (
                            <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1.5 text-[11px]" data-testid={`card-prediction-${m.id}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-zinc-500">Expected</span>
                                <span className="font-medium text-zinc-800">{m.expected_completion_display}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-zinc-500">Confidence</span>
                                <span className={`font-medium capitalize ${
                                  m.confidence_level === "high" ? "text-green-700" :
                                  m.confidence_level === "low" ? "text-red-700" : "text-amber-700"
                                }`}>
                                  {m.confidence_level} ({m.confidence_score}%)
                                </span>
                              </div>
                              {m.likely_delay_top && (
                                <div className="flex items-center justify-between">
                                  <span className="text-zinc-500">Delay risk</span>
                                  <span className="font-medium text-red-700 truncate ml-2 max-w-[140px]" title={m.likely_delay_top}>
                                    {m.likely_delay_top}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          {m.task_owner_counts && (
                            <div className="mt-2 flex items-center gap-1 flex-wrap text-[9px] uppercase tracking-wider" data-testid={`card-owners-${m.id}`}>
                              {Object.entries(m.task_owner_counts).map(([o, n]) =>
                                n > 0 ? (
                                  <span key={o} className={`px-1.5 py-0.5 ${
                                    o === "client" ? "bg-blue-50 text-blue-800" :
                                    o === "lawyer" ? "bg-purple-50 text-purple-800" :
                                    o === "back_office" ? "bg-zinc-100 text-zinc-700" :
                                    "bg-zinc-50 text-zinc-500"
                                  }`}>
                                    {o.replace("_", " ")}: {n}
                                  </span>
                                ) : null
                              )}
                            </div>
                          )}
                          {m.tasks_total > 0 && (
                            <div className="text-[10px] text-zinc-500 mt-2 font-mono">
                              {m.tasks_done}/{m.tasks_total} tasks done
                            </div>
                          )}
                          {m.value > 0 && (
                            <div className="font-serif text-lg mt-2">{formatZAR(m.value)}</div>
                          )}
                        </Link>
                      </div>
                    );
                  })}
                  {!loading && items.length === 0 && (
                    <div className="text-xs text-zinc-400 px-2 py-4 text-center">—</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr className="text-left">
                <Th>Client</Th><Th>Type</Th><Th>Owners pending</Th><Th>Value</Th><Th>Status</Th><Th>Updated</Th>
              </tr>
            </thead>
            <tbody>
              {filteredMatters.map((m) => (
                <tr key={m.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td>
                    <Link to={`/admin/matters/${m.id}`} className="font-medium hover:underline">
                      {m.client_name}
                    </Link>
                  </Td>
                  <Td className="capitalize">{m.matter_type || m.service_type}</Td>
                  <Td className="text-xs">
                    {m.task_owner_counts && Object.entries(m.task_owner_counts)
                      .filter(([, n]) => n > 0)
                      .map(([o, n]) => `${o}: ${n}`)
                      .join(" · ") || "—"}
                  </Td>
                  <Td>{m.value > 0 ? formatZAR(m.value) : "—"}</Td>
                  <Td className="capitalize">{m.status.replace("_", " ")}</Td>
                  <Td className="text-zinc-500">{new Date(m.updated_at).toLocaleString()}</Td>
                </tr>
              ))}
              {filteredMatters.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No matters yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 text-overline text-zinc-500 font-normal">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
